<template>
    <div>
        <div class="table-header-buttons-container">

            <template v-if="privilegeAddCampaign">

                <div class="table-header-buttons-container prepended">
                    <v-btn
                        :elevation="2"
                        type="button" class="btn btn-primary"
                        @click="onAddCampaignButtonClick">
                        <v-icon left>far fa-plus</v-icon>
                        {{ t_add_new_campaign }}
                    </v-btn>

                    <v-btn
                        :elevation="2"
                        type="button" class="btn btn-primary"
                        @click="onAddDynamicCampaignButtonClick">
                        <v-icon left>far fa-plus</v-icon>
                        {{ t_add_new_dynamic_campaign }}
                    </v-btn>
                </div>

            </template>

            <div class="table-header-buttons-container">
                <template v-if="privilegeDeleteCampaign">
                    <transition name="fade">
                        <v-btn-wrapper
                            v-if="someEntriesSelected"
                            :disabled="loading"
                            :elevation="2"
                            @click="onMultiDeleteButtonClick">
                            <v-icon left>far fa-trash</v-icon>
                            {{ t_delete_selected_campaigns }}
                        </v-btn-wrapper>
                    </transition>
                </template>
            </div>
        </div>

        <table-common
            :allFilterableColumnsSelected="allFilterableColumnsSelected"
            :columnDefaults="columnDefaults"
            :columnOptions="columnOptions"
            :columnsToHideInCardMode="['preview', 'actions']"
            :entries="entriesWithOptionalHidden"
            :filters="filters"
            :filtersEmpty="filtersEmpty"
            :getEditLink="getEditLink"
            :hiddenEntriesDisplayed="hiddenEntriesDisplayed"
            :loading="loading"

            :localStorageKeyPrefix="localStorageKeyPrefix"

            :noEntriesFromBackend="noEntriesFromBackend"
            :openMenuId.sync="openMenuId"

            :rowDropdownItems="rowDropdownItems"
            :selectedColumns.sync="selectedColumns"
            :selectedEntries="selectedEntries"

            :selectedRowIdsMap="selectedRowIdsMap"
            :style="{
                '--thumbnail-height-ul': previewSizeModel,
                'margin-block-start': '0.75rem'
            }"

            sort-field-initial="updated_at_millis"
            :sort-order-initial="-1"

            :tableElementRef="localStorageKeyPrefix"

            :textLoading="t_loading"
            :textNoEntriesFromBackend="t_no_data_available_in_table"
            :textNoEntriesFound="t_no_matching_records_found"
            @clearFilters="clearFilters"

            @refresh="loadCampaigns"

            @rowSelect="onTableRowSelect"

            @rowUnselect="onTableRowUnselect"

            @update:selectedEntries="onSelectedEntriesChange"
            @update:renderedEntriesMap="onRenderedEntriesUpdate"

            @update:displayedColumns="displayedColumns = $event"

            @update:openMenuAtEnd="openMenuAtEnd = $event"
            @update:cardsDisplayMode="openMenuId = null"

            @row-contextmenu="onRowContextMenu"


        ><!--@update:cardsDisplayMode="() => {if (popperInstanceRow) popperInstanceRow.destroy()}"-->
            <!--
            @rowSelectAll="onTableRowSelectAll"
            @rowUnselectAll="onTableRowUnselectAll"-->
            <!--@selectedColumnsChange="onSelectedColumnsChange"-->
            <!--@selectedEntries="onSelectedEntriesChange"-->

            <!--suppress VueUnrecognizedSlot -->
            <template #default="{ rowDropdownClick }">

                <column :footer="selectedEntriesLengthString"
                        :header="selectedEntriesLengthString"
                        :headerLabel="selectedEntriesHeaderLabel"
                        bodyClass="column-padded-start"
                        filterHeaderClass="column-padded-start"
                        footerClass="column-padded-start"
                        headerClass="column-no-resize column-padded-start column-justify-center"
                        selectionMode="multiple" />

                <column :footer="columnDefaults[0].label"
                        :header="columnDefaults[0].label"
                        :showClearButton="false"
                        :showFilterMenu="false"
                        bodyClass="column-editable"
                        field="name"
                        filterHeaderClass="column-narrow"
                        sortField="name_sortable"
                        sortable
                        sortIcon="far fa-sm fa-a"
                >

                    <!--suppress VueUnrecognizedSlot -->
                    <template #filter="{filterModel,filterCallback}">
                        <filter-autocomplete v-model="filters.name.value"
                                             :items="namesWithUserEntered"
                                             :placeholder="`${t_filter} ${columnDefaults[0].label}`"
                                             @update:userEnteredItems="userEnteredItemsMap.name = $event" />
                    </template>
                    <!--suppress VueUnrecognizedSlot -->
                    <template #body="{data}">
                        <table-editable-name :data="data"
                                             :editPrivilege="editPrivilege"
                                             :is-editing="cellEditValues[data.id] != undefined"
                                             :value="cellEditValues[data.id] ?? ''"
                                             @cellEditCancel="cellEditCancel(data.id)"
                                             @cellEditConfirm="cellEditConfirm(data.id)"
                                             @cellEditEnable="cellEditEnable(data)"
                                             @input="cellEditValues[data.id] = $event"
                        />
                    </template>
                </column>

                <column :footer="columnDefaults[1].label"
                        :header="columnDefaults[1].label"
                        :hidden="!displayedColumns['preview']"
                        :showClearButton="false"
                        :showFilterMenu="false"
                        bodyClass="column-preview"
                        field="preview"
                        filterHeaderClass="column-narrow"><!--
                        :styles="{ 'max-width':'200px', 'width': '200px'}"-->

                    <!--suppress VueUnrecognizedSlot -->
                    <template #filter>
                        <v-select-custom
                            v-model="previewSizeModel"
                            :items="previewSizeItems"
                            :aria-label="columnDefaults[1].label"
                        >
                            <!-- For some unknown reason, the contents of this particular select are shown with
                                 the class 'v-select__selection--comma', which applies margin on top,
                                 moving the text below the middle point (unsightly).
                                 Overwriting the selection template seems to work correctly. -->
                            <template #selection="{ item }">
                                {{ item.text }}
                            </template>
                        </v-select-custom>
                    </template>
                    <!--suppress VueUnrecognizedSlot -->
                    <template #body="{data}">
                        <advanced-thumbnail
                            :alt="data.name"
                            :aspectRatio="data.parameters.aspectRatio"
                            :elements="data.elements"
                            :href="getEditLink(data.id)"
                            :thumb="data.last_thumb" />
                    </template>

                </column>

                <column :footer="columnDefaults[2].label"
                        :header="columnDefaults[2].label"
                        :hidden="!displayedColumns['screen_info']"
                        :showClearButton="false"
                        :showFilterMenu="false"
                        field="screen_info"
                        filterHeaderClass="column-narrow"
                        headerClass="sort-icon-numeric"
                        sortField="parameters.aspectRatio"
                        sortable
                        :sortOrder="-1"
                        sortIcon="far fa-sm fa-1"
                >
                    <!--suppress VueUnrecognizedSlot -->
<!--                    <template #filter="{filterModel,filterCallback}">
                        <filter-autocomplete v-model="filters.screen_info.value"
                                             :items="screenInfoWithUserEntered"
                                             :placeholder="`${t_filter} ${columnDefaults[2].label}`"
                                             @update:userEnteredItems="userEnteredItemsMap.screen_info = $event" />
                    </template>-->
                    <!--suppress VueUnrecognizedSlot -->
                    <template #body="{data}">
                        {{ data.screen_info }}
                    </template>
                </column>

                <column :footer="columnDefaults[3].label"
                        :header="columnDefaults[3].label"
                        :hidden="!displayedColumns['usergroups']"
                        :showClearButton="false"
                        :showFilterMenu="false"
                        field="usergroups"
                        filterHeaderClass="column-narrow">
                    <!--:filterMatchModeOptions="filterMatchModes"-->
                    <!--suppress VueUnrecognizedSlot -->
                    <template #filter="{filterModel,filterCallback}">
                        <filter-autocomplete v-model="filters.usergroups.value"
                                             :items="usergroupsWithUserEntered"
                                             :placeholder="`${t_filter} ${columnDefaults[3].label}`"
                                             :super-tags="true"
                                             @update:userEnteredItems="userEnteredItemsMap.usergroups = $event" />
                    </template>
                    <!--suppress VueUnrecognizedSlot -->
                    <template #body="{data}">
                        <div class="tags-container">
                            <table-tag v-for="usergroup in data.usergroups"
                                       :key="usergroup.id">
                                {{ usergroup.name }}
                            </table-tag>
                        </div>
                    </template>
                </column>

                <column :footer="columnDefaults[4].label"
                        :header="columnDefaults[4].label"
                        :hidden="!displayedColumns['tags']"
                        :showClearButton="false"
                        :showFilterMenu="false"
                        field="tags"
                        filterHeaderClass="column-narrow">
                    <!--suppress VueUnrecognizedSlot -->
                    <template #filter="{filterModel,filterCallback}">
                        <filter-autocomplete v-model="filters.tags.value"
                                             :items="tagsWithUserEntered"
                                             :placeholder="`${t_filter} ${columnDefaults[4].label}`"
                                             :super-tags="true"
                                             @update:userEnteredItems="userEnteredItemsMap.tags = $event" />
                    </template>
                    <!--suppress VueUnrecognizedSlot -->
                    <template #body="{data}">
                        <div class="tags-container">
                            <table-super-tag v-for="tag in data.tags"
                                             :key="tag.id"
                                             :value="tag.name" />
                        </div>
                    </template>
                </column>

<!--                <column :footer="columnDefaults[5].label"
                        :header="columnDefaults[5].label"
                        :hidden="!displayedColumns['duration_text']"
                        :showClearButton="false"
                        :showFilterMenu="false"
                        field="duration_text"
                        filterHeaderClass="column-narrow"
                        headerClass="sort-icon-numeric"
                        sortField="duration_seconds"
                        sortable>
                    &lt;!&ndash;suppress VueUnrecognizedSlot &ndash;&gt;
                    <template #filter="{filterModel,filterCallback}">
                        <filter-autocomplete v-model="filters.duration_text.value"
                                             :items="durationWithUserEntered"
                                             :placeholder="`${t_filter} ${columnDefaults[5].label}`"
                                             @update:userEnteredItems="userEnteredItemsMap.duration_text = $event" />
                    </template>
                </column>-->

                <column :footer="columnDefaults[5].label"
                        :header="columnDefaults[5].label"
                        :hidden="!displayedColumns['validity_text']"
                        :showClearButton="false"
                        :showFilterMenu="false"
                        bodyClass="text-center"
                        field="validity_text"
                        filterField="validity_text"
                        filterHeaderClass="column-narrow"
                        headerClass="sort-icon-numeric"
                        sortField="valid_from_millis"
                        sortable
                        :sortOrder="-1"
                        sortIcon="far fa-calendar-day"
                >
                    <!--suppress VueUnrecognizedSlot -->
<!--                    <template #filter="{filterModel,filterCallback}">
                        <filter-autocomplete v-model="filters.validity_text.value"
                                             :items="validityWithUserEntered"
                                             :placeholder="`${t_filter} ${columnDefaults[6].label}`"
                                             @update:userEnteredItems="userEnteredItemsMap.validity_text = $event" />
                    </template>-->
                    <!--suppress VueUnrecognizedSlot -->
                    <template #body="{data}">
                        <div :class="data.parameters['validity-status']">
                            {{ data.validity_text }}
                        </div>
                    </template>
                </column>

                <column :footer="columnDefaults[6].label"
                        :header="columnDefaults[6].label"
                        :hidden="!displayedColumns['updated_at']"
                        :showClearButton="false"
                        :showFilterMenu="false"
                        field="updated_at_local"
                        filterHeaderClass="column-narrow"
                        sortField="updated_at_millis"
                        sortable
                        :sortOrder="-1"
                        sortIcon="far fa-clock"
                >
                    <!--suppress VueUnrecognizedSlot -->
<!--                    <template #filter="{filterModel,filterCallback}">
                        <filter-autocomplete v-model="filters.updated_at.value"
                                             :items="updatedAtWithUserEntered"
                                             :placeholder="`${t_filter} ${columnDefaults[7].label}`"
                                             @update:userEnteredItems="userEnteredItemsMap.updated_at = $event" />
                    </template>-->
                    <!--suppress VueUnrecognizedSlot -->
<!--                    <template #body="{data}">-->
<!--                        {{ data.updated_at }}-->
<!--                    </template>-->
                </column>

                <column :footer="columnDefaults[7].label"
                        :header="columnDefaults[7].label"
                        :hidden="!displayedColumns['created_at']"
                        :showClearButton="false"
                        :showFilterMenu="false"
                        field="created_at_local"
                        filterHeaderClass="column-narrow"
                        sortField="created_at_millis"
                        sortable
                        :sortOrder="-1"
                        sortIcon="far fa-clock"
                >

                    <!--suppress VueUnrecognizedSlot -->
<!--                    <template #filter="{filterModel,filterCallback}">
                        <filter-autocomplete v-model="filters.created_at.value"
                                             :items="createdAtWithUserEntered"
                                             :placeholder="`${t_filter} ${columnDefaults[8].label}`"
                                             @update:userEnteredItems="userEnteredItemsMap.created_at = $event" />
                    </template>-->
                </column>

                <column :footer="columnDefaults[8].label"
                        :header="columnDefaults[8].label"
                        :showClearButton="false"
                        :showFilterMenu="false"
                        :styles="{ 'line-height':'unset'}"
                        bodyClass="column-padded-end column-frozen-right"
                        filterHeaderClass="column-padded-end column-frozen-right no-min-width"
                        footerClass="column-padded-end column-frozen-right"
                        headerClass="column-padded-end column-frozen-right"> <!--'min-width':'8rem'
                        filterHeaderStyle="background-color: #f00"-->
                    <!--suppress VueUnrecognizedSlot -->
                    <template #filter>
                        <square-button
                            :buttonClasses="[{ 'filter-clear-button-invisible': filtersEmpty }, 'fade-enter-active']"
                            :tooltip-text="t_clear_filters"
                            flat
                            icon="fas fa-filter-slash"
                            @click="clearFilters" />
                    </template>
                    <!--suppress VueUnrecognizedSlot -->
                    <template #body="{data}">

                        <div :id="`btnSet${data.id}`"
                             :class="{'table-action-button-menu-open': openMenuId === data.id}"
                             class="table-action-button"
                             style="white-space: nowrap; display: inline-flex; width: fit-content;  flex-direction: column;">
                            <!--split-button-->

                            <square-button :tooltipText="columnDefaults[8].label"
                                           flat
                                           icon="fas fa-ellipsis-vertical"
                                           @click="rowDropdownClick(data.id, true)" />

                            <list-dropdown-menu :id="data.id"
                                                :attach="`#rowDropMenu${data.id}`"
                                                :items="rowDropdownItems"
                                                :value="openMenuId === data.id"
                                                rounded />

                            <portal>
                                <div :id="`rowDropMenu${data.id}`"
                                     class="popper-menu-anchor popper-menu-hide-under-navbar" />
                            </portal>
                        </div>
                    </template>
                </column>
            </template>
        </table-common>
    </div>
</template>

<script>
import { defineComponent } from 'vue'
import CampaignService from '../../../services/CampaignService'
import entriesCommonMixin from '../../../mixins/entriesCommonMixin';
import entriesLoaderMixin from '../../../mixins/entriesLoaderMixin';
import tableMixin from '../../../mixins/tableMixin'
import popperContextMenuMixin from '../../../mixins/popperContextMenuMixin';

import TableCommon from '../common/table/table-common.vue'
import TableTag from '../common/table/table-tag.vue'
import TableSuperTag from '../common/table/table-super-tag.vue'
import FilterAutocomplete from '../common/table/filter-autocomplete.vue'
import Column from '../common/PrimeVue/Column.vue'
import ListDropdownMenu from '../common/list-dropdown-menu.vue'
import TableEditableName from '../common/table/table-editable-name.vue'
import AdvancedThumbnail from '../common/advanced-thumbnail.vue'
import SquareButton from '../common/squareButton.vue'
import toaster from '../../common/toaster'

import { Portal } from '@linusborg/vue-simple-portal'

import '../../../../prototypeExtensions/String'

export default defineComponent({
    components: {
        SquareButton,
        AdvancedThumbnail,
        TableEditableName,
        ListDropdownMenu,
        TableCommon,
        TableTag,
        TableSuperTag,
        FilterAutocomplete,
        Column,
        Portal
    },
    mixins: [
        entriesCommonMixin,
        entriesLoaderMixin,
        tableMixin,
        popperContextMenuMixin,
    ],
    props: {
        /** @type {'1' | undefined} */
        privilegeAddCampaign: String,
        /** @type {'1' | undefined} */
        privilegeDeleteCampaign: String,
        /** @type {'1' | undefined} */
        privilegeImportExportCampaign: String
    },
    data() {
        return {
            campaignService: new CampaignService(),

            editPrivilege: false,
            previewsUrl: null,

            /** @type {{label: string, value: string, disabled?: boolean}[]} */
            columnDefaults: [
                {
                    label: Lang.langGet('Name').capitalize(),
                    value: 'name',
                    disabled: true
                },
                {
                    label: Lang.langGet('Preview').capitalize(),
                    value: 'preview'
                },
                {
                    label: Lang.langGet('Resolution').capitalize(),
                    value: 'screen_info'
                },
                {
                    label: Lang.langGet('Usergroups').capitalize(),
                    value: 'usergroups'
                },
                {
                    label: Lang.langGet('Tags').capitalize(),
                    value: 'tags'
                },
                /*{
                    label: Lang.langGet('Duration').capitalize(),
                    value: 'duration_text'
                },*/
                {
                    label: Lang.langGet('Validity').capitalize(),
                    value: 'validity_text'
                },
                {
                    label: Lang.langGet('Last modified').capitalize(),
                    value: 'updated_at'
                },
                {
                    label: Lang.langGet('Created at').capitalize(),
                    value: 'created_at'
                },
                {
                    label: Lang.langChoice('Action|Actions', 4).capitalize(),
                    value: 'actions',
                    disabled: true
                }
            ],

            /** @type {Record.<string, {matchMode: string, value: string[], num?: true, fieldName?: string}>} */
            filters: {
                name: {
                    matchMode: 'containsArray_OR',
                    value: []
                },
                /*screen_info: {
                    matchMode: 'containsArray_OR',
                    value: []
                },*/
                usergroups: {
                    matchMode: 'arrayContainsNameArray_OR',
                    value: []
                },
                tags: {
                    matchMode: 'arrayContainsNameArray_OR',
                    value: []
                },
                /*duration_text: {
                    matchMode: 'containsArray_OR',
                    value: []
                },*/
                /*validity_text: {
                    matchMode: 'containsArray_OR',
                    value: []
                },*/
                /*updated_at: {
                    matchMode: 'containsArray_OR',
                    value: []
                },*/
                /*created_at: {
                    matchMode: 'containsArray_OR',
                    value: []
                }*/
            },

            userEnteredItemsMap: {
                name: [],
                screen_info: [],
                usergroups: [],
                tags: [],
                /*duration_text: [],*/
                validity_text: [],
                updated_at: [],
                created_at: []
            },

            rowDropdownItems: [],

            localStorageKeyPrefix: 'dt-campaigns',

            t_add_new_campaign: Lang.langGet('Add new campaign'),
            t_add_new_dynamic_campaign: Lang.langGet('Add new dynamic campaign'),
            t_delete_selected_campaigns: Lang.langGet('Delete Selected Campaigns'),
            t_export_selected_campaigns: Lang.langGet('Export Selected Campaigns'),

            t_failed_to_modify_campaign: Lang.langGet('Failed to modify campaign in database.'),
            t_failed_to_edit_campaign: Lang.langGet('Failed to edit campaign:'),
            t_failed_to_export_campaign: Lang.langGet('Failed to export campaign!'),
            t_failed_to_copy_campaign: Lang.langGet('Failed to copy campaign!'),

            t_campaign_was_copied_successfully: Lang.langGet('Campaign was copied successfully.'),
            t_campaign_was_exported_successfully: Lang.langGet('Campaign was exported successfully.'),

            /** Used by entriesLoaderMixin.js */
            t_data_load_error: Lang.langGet("Failed to load campaigns!"),
        }
    },
    created() {

        this.rowDropdownItems.push({
            label: this.t_edit,
            icon: 'far fa-pencil',
            //command: (id) => {
            //    console.log('clicked Edit for id', id)
            //},
            href: (id) => this.getEditLink(id)
        })

        if (this.privilegeDeleteCampaign) {
            this.rowDropdownItems.push({
                label: this.t_delete,
                icon: 'far fa-trash',
                class: 'menu-button-delete',
                command: (id) => {
                    EaseEventBus.fire('deleteContentDialog', { entries: [{id, name: this.entriesMap[id].name}] });
                }
            })
        }

        if (this.privilegeAddCampaign) {
            this.rowDropdownItems.push({
                label: this.t_create_copy,
                icon: 'far fa-copy',
                command: (id) => {
                    //console.log('clicked Copy for id', id)
                    this.copyCampaign(id)
                }
            })
        }

        this.rowDropdownItems.push({
            label: this.t_preview,
            icon: 'far fa-image',
            command: (id) => {
                //console.log('clicked Preview for id', id)
                this.previewCampaign(id)
            }
        })

        if (this.privilegeImportExportCampaign) {
            this.rowDropdownItems.push({
                label: this.t_export,
                icon: 'far fa-arrow-down-to-bracket',
                command: (id) => {
                    //console.log('clicked Export for id', id)
                    this.exportCampaign(id)
                }
            })
        }

        this.loadCampaigns()

        EaseEventBus.listen('onCampaignDeleted', (ids) => {
            const idArray = Array.isArray(ids) ? ids : [ids]
            idArray.forEach(id => {
                //console.log('deleting entry', id)
                delete this.entriesMap[id]
            })
            this.entriesMap = { ...this.entriesMap }
            this.refreshLocalCampaigns()
        })

        EaseEventBus.listen('reloadData', this.loadCampaigns)
    },
    methods: {
        async loadCampaigns() {
            const dataResponse = await this.loadData(this.campaignService.getCampaigns, this.fieldsToKeep)

            this.editPrivilege = dataResponse.editPrivilege === 1
            this.previewsUrl = dataResponse.previewsUrl
        },
        refreshLocalCampaigns() {
            //console.log('refreshing local campaigns')
            this.loadData(this.entries, this.fieldsToKeep)
        },
        getEditLink(id) {
            return `/content/campaign/edit/${id}/latest`
        },
        /*onSelectedColumnsChange(value) {
            //this.$refs.tableCampaigns.onSelectedColumnsChange(value);
            this.selectedColumns = value
        },*/
        /*onSelectedEntriesChange(value) {
            //console.log('onSelectedEntriesChange', JSON.stringify(value.map(x => x.name), null, 2), this.selectedEntries === value)
            this.selectedEntries = value
        },*/
        onAddCampaignButtonClick() {
            EaseEventBus.fire('showAddContentDialog', {});
        },
        onAddDynamicCampaignButtonClick() {
            EaseEventBus.fire('showAddContentDialog', {dynamic: true});
        },
        onImportButtonClick() {
            EaseEventBus.fire('showModal', 'content-import')
        },
        onExportButtonClick() {
            this.selectedEntries.forEach((selectedEntry) => {
                this.exportCampaign(selectedEntry.id)
            })
        },
        onMultiDeleteButtonClick() {
            EaseEventBus.fire('deleteContentDialog', {entries: this.selectedEntries});
        },
        copyCampaign(campaignId) {
            axios.post('/content/campaign/copy', {
                id: campaignId,
                project: Laravel.activeProject
            }).then(({ status }) => {
                if (status === 401) {
                    toastr.error(this.t_failed_to_copy_campaign)
                    window.location = '/login'
                    return
                }

                toastr.success(this.t_campaign_was_copied_successfully)
                this.loadCampaigns()

            }).catch((error) => {
                toastr.error(`${this.t_failed_to_copy_campaign} ${error}`)
            })
        },
        previewCampaign(campaignId) {
            EaseEventBus.fire('previewCampaign', { id: campaignId, parameters: this.entriesMap[campaignId].parameters })
        },
        exportCampaign(campaignId) {
            axios.post('/content/campaign/export', {
                id: campaignId,
                project: Laravel.activeProject
            }).then(({ data, status }) => {

                if (status === 401) {
                    toastr.error(this.t_failed_to_export_campaign)
                    window.location = '/login'
                    return
                }

                toastr.success(this.t_campaign_was_exported_successfully)
                const link = document.createElement('a')
                link.href = data
                link.download = data.substr(data.lastIndexOf('/') + 1)
                link.click()
                link.remove()

            }).catch((error) => {
                toastr.error(`${this.t_failed_to_export_campaign} ${error}`)
            })
        },
        cellEditConfirm(campaignId) {
            if (!this.editPrivilege) return

            const campaignName = this.cellEditValues[campaignId];
            if(!campaignName) return;

            axios.post(`/content/campaign/edit/${campaignId}/update-name`, {
                project: Laravel.activeProject,
                id: campaignId,
                name: campaignName
            }).then(() => {
                this.rowNameUpdate({ id: campaignId, name: campaignName })
            }).catch((error) => {
                if (error.response.status === 401) {
                    window.location = '/login'
                } else if (error.response.status === 422) {
                    let errorString = this.t_failed_to_edit_campaign + ' '
                    for (let key in error.response.data) {
                        errorString = errorString + error.response.data[key][0] + ' '
                    }
                    toaster.error(errorString)
                } else {
                    toaster.error(this.t_failed_to_modify_campaign)
                }
            }).finally(() => this.cellEditCancel(campaignId))

        }
    },
    computed: {
        fieldsToKeep() {
            const fieldsToKeep = this.columnDefaults.map(columnDefault => columnDefault.value)
            delete fieldsToKeep['actions']
            fieldsToKeep.push(
                'id',
                'parameters',
                'last_thumb',
                'name_sortable',
                'updated_at_millis',
                'created_at_millis',
                'validity_text',
                'valid_from_millis',
                'dynamic',
                'elements',
                'updated_at_local',
                'created_at_local',
                /*'duration_text',
                'duration_seconds'*/
            )
            return fieldsToKeep
        },
        namesWithUserEntered() {
            return this.filteredItemsWithUserEntered(this.fieldValuesMap.name, this.userEnteredItemsMap.name);
        },
        /*screenInfoWithUserEntered() {
            return this.filteredItemsWithUserEntered(this.fieldValuesMap.screen_info, this.userEnteredItemsMap.screen_info, true);
        },*/
        usergroupsWithUserEntered() {
            return this.filteredItemsWithUserEntered(this.fieldValuesMap.usergroups, this.userEnteredItemsMap.usergroups);
        },
        tagsWithUserEntered() {
            let returnValue = [{
                value: '§hidden',
                disabled: !this.anyEntriesHidden
            }, {
                value: '§imported',
                disabled: !this.anyEntriesImported
            }];
            const tagItems = this.filteredItemsWithUserEntered(this.fieldValuesMap.tags, this.userEnteredItemsMap.tags);

            if (tagItems) {
                returnValue = returnValue.concat(tagItems)
            }
            return returnValue
        },
        /*durationWithUserEntered() {
            return this.filteredItemsWithUserEntered(this.fieldValuesMap.duration_text, this.userEnteredItemsMap.duration_text, true);
        },*/
        /*validityWithUserEntered() {
            return this.filteredItemsWithUserEntered(this.fieldValuesMap.validity_text, this.userEnteredItemsMap.validity_text);
        },*/
        /*updatedAtWithUserEntered() {
            return this.filteredItemsWithUserEntered(this.fieldValuesMap.updated_at, this.userEnteredItemsMap.updated_at);
        },*/
        /*createdAtWithUserEntered() {
            return this.filteredItemsWithUserEntered(this.fieldValuesMap.created_at, this.userEnteredItemsMap.created_at);
        }*/

    },
    mounted() {
        setTimeout(() => {
            document.querySelector('input.dz-hidden-input').remove()
        }, 50)
    }
    /*watch: {
        tagsWithUserEntered(val) {
            console.log('tagsWithUserEntered', val)
        },
        'userEnteredItemsMap.tags'(val) {
            console.log('userEnteredItemsMap.tags', val)
        }
    }*/
})
</script>

<style>
body {
    /* Disable body min-width on this page, since it's responsive */
    min-width: unset !important;
}
</style>

